import { Group, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import { Fragment, useEffect, useRef, useState } from "react";
import AiAssistantIcon from "components/AiAssistantIcon/AiAssistantIcon";
import ActionCreatedWidget from "./ActionCreatedWidget";
import AssistantReviewWidgets from "./AssistantReviewWidgets";
import AssistantSourceBadges from "./AssistantSourceBadges";
import css from "./MicChatDrawer.module.scss";

const stripOuterSingleEmphasis = (text) => {
  const trimmedText = text.trim();
  if (
    trimmedText.length > 2 &&
    trimmedText.startsWith("*") &&
    trimmedText.endsWith("*") &&
    !trimmedText.startsWith("**") &&
    !trimmedText.endsWith("**")
  ) {
    return {
      text: trimmedText.slice(1, -1).trim(),
      isEmphasized: true,
    };
  }

  return {
    text: trimmedText,
    isEmphasized: false,
  };
};

const renderInlineMarkdown = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={`${part}-${index}`} className={css.messageStrong}>
          {part.slice(2, -2)}
        </strong>
      );
    }

    return <Fragment key={`${part}-${index}`}>{part}</Fragment>;
  });
};

const FormattedAssistantMessage = ({ content }) => {
  const blocks = [];
  let paragraphLines = [];
  let bulletLines = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    blocks.push({ type: "paragraph", lines: paragraphLines });
    paragraphLines = [];
  };

  const flushBullets = () => {
    if (!bulletLines.length) return;
    blocks.push({ type: "list", items: bulletLines });
    bulletLines = [];
  };

  String(content || "")
    .split("\n")
    .forEach((rawLine) => {
      const line = rawLine.trim();

      if (!line) {
        flushParagraph();
        flushBullets();
        return;
      }

      const bulletMatch = line.match(/^[-*]\s+(.+)$/);
      if (bulletMatch) {
        flushParagraph();
        bulletLines.push(bulletMatch[1].trim());
        return;
      }

      flushBullets();
      paragraphLines.push(line);
    });

  flushParagraph();
  flushBullets();

  return (
    <div className={css.formattedMessage}>
      {blocks.map((block, blockIndex) => {
        if (block.type === "list") {
          return (
            <ul key={`list-${blockIndex}`} className={css.messageList}>
              {block.items.map((item, itemIndex) => (
                <li key={`${item}-${itemIndex}`}>
                  {renderInlineMarkdown(stripOuterSingleEmphasis(item).text)}
                </li>
              ))}
            </ul>
          );
        }

        const paragraph = stripOuterSingleEmphasis(block.lines.join(" "));
        return (
          <p
            key={`paragraph-${blockIndex}`}
            className={`${css.messageParagraph} ${
              paragraph.isEmphasized ? css.messageEmphasis : ""
            }`}
          >
            {renderInlineMarkdown(paragraph.text)}
          </p>
        );
      })}
    </div>
  );
};

const ChatMessage = ({ message, onSelectSource }) => {
  const isUser = message.role === "user";
  const createdActions = Array.isArray(message.createdActions)
    ? message.createdActions
    : [];

  return (
    <Group
      className={isUser ? css.messageRowUser : css.messageRowAssistant}
      align="flex-end"
      noWrap
    >
      <Paper
        radius="lg"
        px="md"
        py="sm"
        className={
          isUser
            ? css.userBubble
            : `${css.assistantBubble} ${message.error ? css.errorBubble : ""}`
        }
      >
        {isUser || message.error ? (
          <Text size="sm" className={css.messageText}>
            {message.content}
          </Text>
        ) : (
          <FormattedAssistantMessage content={message.content} />
        )}
        {!isUser &&
          !message.error &&
          createdActions.map((action, index) => (
            <ActionCreatedWidget
              key={`${action.text}-${action.dueDateIso || "none"}-${index}`}
              action={action}
            />
          ))}
        {!isUser && !message.error && (
          <AssistantReviewWidgets message={message} />
        )}
        {!isUser && !message.error && (
          <AssistantSourceBadges
            sources={message.sources}
            onSelectSource={onSelectSource}
          />
        )}
      </Paper>
    </Group>
  );
};

const EmptyState = () => (
  <div className={css.emptyState}>
    <AiAssistantIcon className={css.emptyIcon} />
    <Text weight={700} size="lg" color="#253047">
      Start with a note, question, or meeting follow-up.
    </Text>
    <Text size="sm" color="#667085" className={css.emptyCopy}>
      Ask for a recap, action list, message draft, or next-step plan.
    </Text>
  </div>
);

const LABEL_FADE_OUT_MS = 120;
const DEFAULT_THINKING_LABEL = "GainTime is thinking...";

const PoppingDots = () => (
  <span className={css.thinkingDots} aria-hidden="true">
    {[0, 1, 2].map((dot) => (
      <span key={dot} className={css.thinkingDot} />
    ))}
  </span>
);

const SmoothProgressLabel = ({ label }) => {
  const [displayedLabel, setDisplayedLabel] = useState(label);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (label === displayedLabel) return undefined;

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => {
      setDisplayedLabel(label);
      setIsVisible(true);
    }, LABEL_FADE_OUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [displayedLabel, label]);

  return (
    <Text
      size="sm"
      color="#373B54"
      className={`${css.thinkingText} ${
        isVisible ? css.thinkingTextVisible : css.thinkingTextHidden
      }`}
    >
      {displayedLabel}
    </Text>
  );
};

const ThinkingMessage = ({ progress }) => {
  const label =
    typeof progress?.label === "string" && progress.label.trim()
      ? progress.label.trim()
      : DEFAULT_THINKING_LABEL;

  return (
    <Group align="flex-end" noWrap className={css.messageRowAssistant}>
      <Paper radius="lg" px="md" py="sm" className={css.thinkingBubble}>
        <div className={css.thinkingContent}>
          <PoppingDots />
          <SmoothProgressLabel label={label} />
        </div>
      </Paper>
    </Group>
  );
};

const ChatMessageList = ({
  messages,
  isAiThinking,
  assistantProgress,
  onSelectSource,
}) => {
  const bottomRef = useRef(null);
  const hasMessages = messages.length > 0;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isAiThinking, assistantProgress]);

  return (
    <ScrollArea className={css.messagesScrollArea} offsetScrollbars>
      <Stack spacing="md" className={css.messageStack}>
        {hasMessages ? (
          messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              onSelectSource={onSelectSource}
            />
          ))
        ) : (
          <EmptyState />
        )}
        {isAiThinking && <ThinkingMessage progress={assistantProgress} />}
        <div ref={bottomRef} />
      </Stack>
    </ScrollArea>
  );
};

export default ChatMessageList;
